/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { generate as generateUniqueId } from 'randomstring'
import { IconButton, Button } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { RenderAdvancedFieldProps } from 'components/AdvancedFieldArray/AdvancedField'
import Input from 'components/Input'
import { Popper } from 'components/Popper'
import { ReactComponent as CheckIcon } from 'assets/icons/check.svg'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as CheckBoxIcon } from 'assets/icons/check-box.svg'
import { ReactComponent as CloseBoxIcon } from 'assets/icons/close-box.svg'
import { ReactComponent as TextBoxIcon } from 'assets/icons/text-box.svg'
import * as classes from './styles'

const BOOSTER_PLAN = 'BOOSTER_PLAN'
const BOOTCAMP_PLAN = 'BOOTCAMP_PLAN'

type Plan = typeof BOOSTER_PLAN | typeof BOOTCAMP_PLAN

type ComparisonValue = 'text' | 'check' | 'cross'

export type FieldValue = { id: string; featureName: string; description?: string } & Record<
  Plan,
  {
    type: ComparisonValue
    value?: string
  }
>

export const validateFeature = (value?: FieldValue) => {
  const isValidPlanValue = (plan: Plan) => {
    if (value?.[plan]?.type === 'text') {
      return !!value?.[plan].value
    }

    return true
  }

  if (!isValidPlanValue(BOOSTER_PLAN)) {
    return 'Booster plan field is required'
  }

  if (!isValidPlanValue(BOOTCAMP_PLAN)) {
    return 'Bootcamp field is required'
  }

  return undefined
}

export const getEmptyItem = (): FieldValue => ({
  id: generateUniqueId(),
  featureName: '',
  [BOOSTER_PLAN]: {
    type: 'text',
  },
  [BOOTCAMP_PLAN]: {
    type: 'text',
  },
})

type Props = RenderAdvancedFieldProps<FieldValue>

export const FeatureArrayField: FC<Props> = ({ name, value: valueProp, onChange, editable }) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const value = valueProp!

  const handleOptionClick = (plan: Plan, type: ComparisonValue, onClose: () => void) => () => {
    onChange({
      ...value,
      [plan]: {
        type: type,
      },
    })
    onClose()
  }

  const getFieldByComparisonType = (plan: Plan) => {
    const type = value?.[plan]?.type

    if (type === 'cross') {
      return <CrossIcon />
    }

    if (type === 'check') {
      return <CheckIcon />
    }

    return editable ? (
      <Input
        name={`${name}.${plan}.value`}
        value={value?.[plan]?.value || ''}
        onChange={(inputValue) => {
          onChange({
            ...value,
            [plan]: {
              type: value?.[plan]?.type,
              value: inputValue,
            },
          })
        }}
        placeholder="Feature value"
        size="small"
        css={classes.input}
        required
      />
    ) : (
      value?.[plan]?.value || ''
    )
  }

  const renderPopperContent = (plan: Plan, onClose: () => void) => (
    <ul css={classes.popperList}>
      <li>
        <Button css={classes.popperListButton} variant="outlined" onClick={handleOptionClick(plan, 'text', onClose)}>
          <TextBoxIcon />
          Only Text
        </Button>
      </li>
      <li>
        <Button css={classes.popperListButton} variant="outlined" onClick={handleOptionClick(plan, 'check', onClose)}>
          <CheckBoxIcon />
          Check Mark
        </Button>
      </li>
      <li>
        <Button css={classes.popperListButton} variant="outlined" onClick={handleOptionClick(plan, 'cross', onClose)}>
          <CloseBoxIcon />
          Cross Mark
        </Button>
      </li>
    </ul>
  )

  return editable ? (
    <div css={classes.featureArrayFieldWrapper}>
      <div css={classes.featureArrayFieldItem}>
        <div css={classes.featureNameAndDecriptionWrapper}>
          <Input
            name={`${name}.featureName`}
            value={value?.featureName || ''}
            onChange={(inputValue) => {
              onChange({
                ...value,
                featureName: inputValue,
              })
            }}
            placeholder="Feature name"
            size="small"
            css={classes.input}
            required
          />
          <Input
            name={`${name}.description`}
            value={value?.description || ''}
            onChange={(inputValue) => {
              onChange({
                ...value,
                description: inputValue,
              })
            }}
            placeholder="Description (optional)"
            size="small"
            css={classes.input}
            multiline
          />
        </div>
      </div>
      <div css={classes.featureArrayFieldItem}>
        <div css={classes.valueWrapper}>{getFieldByComparisonType(BOOSTER_PLAN)}</div>
        <Popper trigger="onClick" getContent={(onClose) => renderPopperContent(BOOSTER_PLAN, onClose)}>
          <IconButton size="small" css={classes.popperButton} data-testid="change-booster-button">
            <AddRoundedIcon />
          </IconButton>
        </Popper>
      </div>

      <div css={classes.featureArrayFieldItem}>
        <div css={classes.valueWrapper}>{getFieldByComparisonType(BOOTCAMP_PLAN)}</div>
        <Popper trigger="onClick" getContent={(onClose) => renderPopperContent(BOOTCAMP_PLAN, onClose)}>
          <IconButton size="small" css={classes.popperButton} data-testid="change-bootcamp-button">
            <AddRoundedIcon />
          </IconButton>
        </Popper>
      </div>
    </div>
  ) : (
    <div css={classes.featureArrayFieldWrapper}>
      <div css={classes.featureArrayFieldItem}>
        <div css={classes.featureNameAndDecriptionWrapper}>
          <span>{value?.featureName}</span>
          {value?.description && <span css={classes.descriptionArrayFieldText}>{value.description}</span>}
        </div>
      </div>
      <div css={classes.featureArrayFieldItem}>
        <div css={classes.valueWrapper}>{getFieldByComparisonType(BOOSTER_PLAN)}</div>
      </div>

      <div css={classes.featureArrayFieldItem}>
        <div css={classes.valueWrapper}>{getFieldByComparisonType(BOOTCAMP_PLAN)}</div>
      </div>
    </div>
  )
}
